export const UAP_ALLOWED_ROLES = new Set(["1", "2"]);

export const getStoredUsuariosProyectosUser = () => {
  try {
    const stored = localStorage.getItem("token");
    return stored ? JSON.parse(stored) : null;
  } catch (error) {
    return null;
  }
};

export const canAccessUsuariosProyectos = (user) =>
  UAP_ALLOWED_ROLES.has(String(user?.rol ?? ""));

export const getProyectoId = (project) =>
  project?.idProyecto ?? project?.idproyectos ?? project?.id ?? "";

export const getProyectoCodigo = (project) =>
  project?.codigo ?? project?.codigoProyecto ?? "";

export const getProyectoNombre = (project) =>
  project?.nombre ?? project?.nombreProyecto ?? "Proyecto sin nombre";

export const getProyectoLabel = (project) =>
  [getProyectoCodigo(project), getProyectoNombre(project)]
    .filter(Boolean)
    .join(" · ");

export const getUsuarioId = (user) =>
  user?.id ?? user?.idUsuario ?? user?.id_usuario ?? "";

export const getUsuarioNombre = (user) => {
  const name = [user?.nombres, user?.apellidos].filter(Boolean).join(" ").trim();
  return name || user?.email || "Usuario sin nombre";
};

export const formatFechaAsignacion = (value) => {
  if (!value) return "—";
  const datePart = String(value).slice(0, 10);
  const match = datePart.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return match ? `${match[3]}/${match[2]}/${match[1]}` : "—";
};

const normalizeText = (value = "") =>
  String(value)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();

export const filterUsuariosAsignados = (users = [], search = "") => {
  const query = normalizeText(search);
  if (!query) return users;

  return users.filter((user) =>
    normalizeText(
      [
        user.nombres,
        user.apellidos,
        user.email,
        user.nombreRol,
        user.cargo,
      ].join(" ")
    ).includes(query)
  );
};

export const getUsuariosProyectosErrorMessage = (error, action = "request") => {
  const status = error?.response?.status;

  if (status === 400) return "Revisa la información e intenta nuevamente.";
  if (status === 401) return "Tu sesión no es válida o ha expirado.";
  if (status === 403) return "No tienes permisos para realizar esta acción.";
  if (status === 404 && action === "unassign") {
    return "La asignación ya no está activa.";
  }
  if (status === 404) {
    return "El proyecto, usuario o asignación ya no está disponible.";
  }
  if (status === 409) {
    return "El usuario ya está asignado o no puede completar la asignación.";
  }
  if (status >= 500) return "No fue posible completar la solicitud.";
  return "No fue posible conectar con el servidor.";
};

export const asignarUsuariosSecuencial = async ({
  idProyecto,
  usuarios,
  assign = null,
}) => {
  if (typeof assign !== "function") {
    throw new Error("Se requiere una función de asignación.");
  }

  const successes = [];
  const failures = [];

  for (const user of usuarios) {
    try {
      await assign(idProyecto, getUsuarioId(user));
      successes.push(user);
    } catch (error) {
      failures.push({
        user,
        message: getUsuariosProyectosErrorMessage(error, "assign"),
      });
    }
  }

  return { successes, failures };
};

export const buildAsignacionesGenerales = (projectResults = {}) =>
  Object.values(projectResults).flatMap(({ project, users = [] }) =>
    users.map((user) => ({
      ...user,
      idProyecto: getProyectoId(project),
      codigoProyecto: getProyectoCodigo(project),
      nombreProyecto: getProyectoNombre(project),
    }))
  );

export const getConteoProyectosPorUsuario = (rows = []) => {
  const projectIdsByUser = new Map();

  rows.forEach((row) => {
    const userId = String(getUsuarioId(row));
    if (!projectIdsByUser.has(userId)) projectIdsByUser.set(userId, new Set());
    projectIdsByUser.get(userId).add(String(row.idProyecto));
  });

  return Object.fromEntries(
    [...projectIdsByUser.entries()].map(([userId, projectIds]) => [
      userId,
      projectIds.size,
    ])
  );
};

export const getResumenAsignaciones = (rows = []) => {
  const projects = new Set(rows.map((row) => String(row.idProyecto)));
  const users = new Set(rows.map((row) => String(getUsuarioId(row))));
  const projectCounts = getConteoProyectosPorUsuario(rows);

  return {
    proyectos: projects.size,
    usuarios: users.size,
    asignaciones: rows.length,
    multiproyecto: Object.values(projectCounts).filter((count) => count > 1).length,
  };
};

export const filterAsignacionesGenerales = (rows = [], filters = {}) => {
  const search = normalizeText(filters.search);

  return rows.filter((row) => {
    const matchesProject =
      !filters.projectId || String(row.idProyecto) === String(filters.projectId);
    const matchesRole = !filters.role || String(row.nombreRol || row.rol) === filters.role;
    const matchesStatus =
      !filters.status ||
      (filters.status === "active" ? Number(row.estadoUsuario) === 1 : Number(row.estadoUsuario) !== 1);
    const matchesSearch =
      !search ||
      normalizeText(
        [
          row.codigoProyecto,
          row.nombreProyecto,
          row.nombres,
          row.apellidos,
          row.email,
          row.nombreRol,
          row.cargo,
        ].join(" ")
      ).includes(search);

    return matchesProject && matchesRole && matchesStatus && matchesSearch;
  });
};
